import type {
  ChatClientState,
  ChatPageContext,
  ConversationMode,
  FlightModeState,
  StreamChatMutationVariables,
  UiAction,
} from '@massilia/queries'

import { create } from 'zustand'
import { syncTabs } from 'zustand-sync-tabs'
import { createJSONStorage, persist } from 'zustand/middleware'

import { isReunionTimezone } from '@/lib/timezone'

const STORAGE_KEY = 'massilia.chat.widget.v2'
const MAX_MESSAGES_TO_KEEP = 50
const MAX_MESSAGE_CHARS = 2000

export interface ChatMessage {
  traceId?: string
  content: string
  feedbackGiven?: 'down' | 'up'
  role: ChatRole
  toolTrace?: { plannerAction?: string; toolsUsed?: string[]; waitingHint?: string }
  uiActions?: UiAction[]
}

type ChatRole = 'assistant' | 'user'

interface ChatWidgetStoreActions {
  canOpenFlightResults: (query: string) => boolean
  closeChat: () => void
  consumeNavigationUiActions: (messageIndex: number) => void
  handleUiButtonClick: (options: HandleUiButtonClickOptions) => Promise<void>
  resetConversation: () => void
  sendMessage: (options: SendMessageOptions) => Promise<void>
  setFeedback: (messageIndex: number, feedback: 'down' | 'up') => void
  setInput: (input: string) => void
  setPageContext: (pageContext: ChatPageContext | undefined) => void
  setShowQuickActions: (show: boolean) => void
  toggleOpen: () => void
}

// UiAction types that have one-shot navigation side-effects. Once consumed
// (i.e. the widget has navigated or applied filters), they must be stripped
// from the persisted message so they do not replay on reload / re-render.
const NAVIGATION_UI_ACTION_TYPES: ReadonlySet<UiAction['type']> = new Set([
  'apply_filters',
  'open_flight_results_structured',
])

interface ChatWidgetStoreState {
  sessionId: string
  chatMode: ConversationMode
  error: null | string
  flightModeState: FlightModeState | undefined
  input: string
  isOpen: boolean
  isSending: boolean
  isWaitingForFirstChunk: boolean
  lastOpenedFlightQuery: string | undefined
  lastOpenedFlightQueryAt: number | undefined
  messages: ChatMessage[]
  pageContext: ChatPageContext | undefined
  showQuickActions: boolean
  waitingStageLabel: string | undefined
}

interface HandleUiButtonClickOptions extends Omit<SendMessageOptions, 'streamAssistantReply'> {
  displayLabel?: string
  streamAssistantReply: (variables: StreamChatMutationVariables) => Promise<unknown>
  value: string
}

interface SendMessageOptions {
  displayText?: string
  isNearBottom?: () => boolean
  onAssistantChunkRendered?: () => void
  streamAssistantReply: (variables: StreamChatMutationVariables) => Promise<unknown>
}

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

const trimMessages = (messages: ChatMessage[]) =>
  messages.length > MAX_MESSAGES_TO_KEEP ? messages.slice(-MAX_MESSAGES_TO_KEEP) : messages

export const useChatWidgetStore = create<ChatWidgetStoreActions & ChatWidgetStoreState>()(
  syncTabs(
    persist(
      (set, get) => ({
        sessionId: generateSessionId(),
        chatMode: 'general' as ConversationMode,
        error: null,
        flightModeState: undefined,
        input: '',
        isOpen: false,
        isSending: false,
        isWaitingForFirstChunk: false,
        lastOpenedFlightQuery: undefined,
        lastOpenedFlightQueryAt: undefined,
        messages: [],
        pageContext: undefined,
        showQuickActions: true,
        waitingStageLabel: undefined,

        closeChat: () => set({ error: null, isOpen: false }),

        consumeNavigationUiActions: (messageIndex: number) =>
          set((current) => {
            const msg = current.messages[messageIndex]
            if (!msg?.uiActions?.length) return current
            const filtered = msg.uiActions.filter((a) => !NAVIGATION_UI_ACTION_TYPES.has(a.type))
            if (filtered.length === msg.uiActions.length) return current
            const updated = [...current.messages]
            updated[messageIndex] = {
              ...msg,
              uiActions: filtered.length > 0 ? filtered : undefined,
            }
            return { messages: updated }
          }),

        canOpenFlightResults: (query: string) => {
          const now = Date.now()
          const { lastOpenedFlightQuery, lastOpenedFlightQueryAt } = get()
          // 30s guard against repeated auto-navigations on stream replays or
          // message-list churn re-emitting the same open_flight_results action.
          const isDuplicate =
            lastOpenedFlightQuery === query &&
            typeof lastOpenedFlightQueryAt === 'number' &&
            now - lastOpenedFlightQueryAt < 30_000
          if (isDuplicate) {
            return false
          }
          set({ lastOpenedFlightQuery: query, lastOpenedFlightQueryAt: now })
          return true
        },

        resetConversation: () =>
          set((current) => ({
            sessionId: generateSessionId(),
            chatMode: 'general',
            error: null,
            flightModeState: undefined,
            input: '',
            isSending: false,
            isWaitingForFirstChunk: false,
            lastOpenedFlightQuery: undefined,
            lastOpenedFlightQueryAt: undefined,
            messages: [],
            // Preserve pageContext: it reflects the current URL, not the
            // conversation. Resetting it would break the next page-aware turn.
            pageContext: current.pageContext,
            showQuickActions: true,
            waitingStageLabel: undefined,
          })),

        /**
         * Called when the user clicks a dynamic button from uiActions.
         * Sets the button value as input and sends it immediately.
         */
        handleUiButtonClick: async (options) => {
          const { isSending } = get()
          if (isSending) return

          set({ input: options.value, showQuickActions: false })
          await get().sendMessage({
            displayText: options.displayLabel,
            isNearBottom: options.isNearBottom,
            onAssistantChunkRendered: options.onAssistantChunkRendered,
            streamAssistantReply: options.streamAssistantReply,
          })
        },

        sendMessage: async (options) => {
          const { sessionId, chatMode, flightModeState, input, isSending, messages, pageContext } =
            get()
          const trimmed = input.trim()
          if (!trimmed || isSending) {
            return
          }

          if (trimmed.length > MAX_MESSAGE_CHARS) {
            set({
              error:
                `Votre message est trop long (${trimmed.length} caractères). ` +
                `Merci de le raccourcir à moins de ${MAX_MESSAGE_CHARS} caractères.`,
            })
            return
          }

          const displayMessage: ChatMessage = {
            content: options.displayText ?? trimmed,
            role: 'user',
          }
          const serverMessage: ChatMessage = { content: trimmed, role: 'user' }
          const nextMessages = trimMessages([...messages, serverMessage])

          const displayMessages = trimMessages([...messages, displayMessage])
          set({
            error: null,
            input: '',
            isSending: true,
            isWaitingForFirstChunk: true,
            // Every new user message is a fresh search opportunity — clear the
            // last-opened debounce so a re-emitted flight result page can navigate.
            lastOpenedFlightQuery: undefined,
            lastOpenedFlightQueryAt: undefined,
            messages: trimMessages([...displayMessages, { content: '', role: 'assistant' }]),
            waitingStageLabel:
              trimmed === 'yes_flight_search'
                ? 'Je prépare la recherche...'
                : chatMode === 'flight_request'
                  ? trimmed === 'flight_confirm'
                    ? 'Je lance la recherche...'
                    : 'Je réfléchis...'
                  : 'Je réfléchis...',
          })

          const clientState: ChatClientState = {
            sessionId,
            clientHints: { isReunionResident: isReunionTimezone() },
            flightModeState,
            mode: chatMode,
            pageContext,
          }

          try {
            let shouldAutoScrollLatest = true
            let pendingToRender = ''
            let rafScheduled = false

            const flushPending = (autoScroll: boolean) => {
              const textToAppend = pendingToRender
              if (!textToAppend) return
              pendingToRender = ''

              set((current) => {
                const updated = [...current.messages]
                const last = updated[updated.length - 1]
                if (last?.role === 'assistant') {
                  updated[updated.length - 1] = {
                    ...last,
                    content: last.content + textToAppend,
                  }
                } else {
                  updated.push({
                    content: textToAppend,
                    role: 'assistant',
                  })
                }
                return { messages: trimMessages(updated) }
              })

              if (autoScroll) {
                options?.onAssistantChunkRendered?.()
              }
            }

            const scheduleFlush = () => {
              if (rafScheduled) return
              rafScheduled = true
              requestAnimationFrame(() => {
                rafScheduled = false
                flushPending(shouldAutoScrollLatest)
              })
            }

            const result = await options.streamAssistantReply({
              clientState,
              messages: nextMessages,
              onChunk: (chunk) => {
                // [LOADING] marker: show spinner again without adding text
                if (chunk === '[LOADING]') {
                  set({ isWaitingForFirstChunk: true })
                  return
                }
                // [MODE] marker: switch chat mode immediately (e.g., flight → general)
                if (chunk.startsWith('[MODE]')) {
                  const newMode = chunk.slice(6) as 'flight_request' | 'general'
                  set({
                    chatMode: newMode,
                    flightModeState: newMode === 'general' ? undefined : get().flightModeState,
                  })
                  return
                }
                // [NEWMSG] marker: start a new assistant message bubble
                if (chunk === '[NEWMSG]') {
                  set((current) => ({
                    isWaitingForFirstChunk: true,
                    messages: trimMessages([
                      ...current.messages,
                      { content: '', role: 'assistant' },
                    ]),
                  }))
                  return
                }
                if (get().isWaitingForFirstChunk) {
                  set({ isWaitingForFirstChunk: false })
                }
                shouldAutoScrollLatest = options?.isNearBottom?.() ?? true
                pendingToRender += chunk
                scheduleFlush()
              },
            })

            flushPending(shouldAutoScrollLatest)

            // Apply meta from the completed stream: nextClientState + uiActions on last message.
            const streamResult = result as {
              content?: string
              meta?: {
                nextClientState?: ChatClientState
                toolTrace?: { plannerAction?: string; toolsUsed?: string[]; waitingHint?: string }
                uiActions?: UiAction[]
              }
            }
            const meta = streamResult?.meta as
              | undefined
              | {
                  nextClientState?: ChatClientState
                  toolTrace?: { plannerAction?: string; toolsUsed?: string[]; waitingHint?: string }
                  traceId?: string
                  uiActions?: UiAction[]
                }

            if (meta) {
              set((current) => {
                const updated = [...current.messages]
                const last = updated[updated.length - 1]
                if (last?.role === 'assistant') {
                  updated[updated.length - 1] = {
                    ...last,
                    traceId: meta.traceId,
                    toolTrace: meta.toolTrace,
                    uiActions: meta.uiActions,
                  }
                }

                const nextState = meta.nextClientState
                return {
                  chatMode: nextState?.mode ?? current.chatMode,
                  flightModeState:
                    nextState?.flightModeState ??
                    (nextState?.mode === 'general' ? undefined : current.flightModeState),
                  messages: trimMessages(updated),
                  showQuickActions: meta.uiActions?.some((a) => a.type === 'show_quick_actions')
                    ? true
                    : current.showQuickActions,
                  waitingStageLabel: meta.toolTrace?.waitingHint ?? current.waitingStageLabel,
                }
              })
            }
          } catch (err) {
            set((current) => {
              const last = current.messages[current.messages.length - 1]
              return {
                error:
                  err instanceof Error ? err.message : "Une erreur s'est produite lors de l'envoi.",
                isWaitingForFirstChunk: false,
                messages:
                  last?.role === 'assistant' && last.content === ''
                    ? current.messages.slice(0, -1)
                    : current.messages,
              }
            })
          } finally {
            set({ isSending: false, isWaitingForFirstChunk: false, waitingStageLabel: undefined })
          }
        },
        setFeedback: (messageIndex, feedback) =>
          set((current) => {
            const updated = [...current.messages]
            const msg = updated[messageIndex]
            if (msg?.role === 'assistant') {
              updated[messageIndex] = { ...msg, feedbackGiven: feedback }
            }
            return { messages: updated }
          }),
        setInput: (input) => set({ input }),
        setPageContext: (pageContext) => set({ pageContext }),
        setShowQuickActions: (show) => set({ showQuickActions: show }),
        toggleOpen: () => set((state) => ({ error: null, isOpen: !state.isOpen })),
      }),
      {
        name: STORAGE_KEY,
        partialize: (state) => ({
          sessionId: state.sessionId,
          chatMode: state.chatMode,
          flightModeState: state.flightModeState,
          isOpen: state.isOpen,
          lastOpenedFlightQuery: state.lastOpenedFlightQuery,
          lastOpenedFlightQueryAt: state.lastOpenedFlightQueryAt,
          messages: trimMessages(state.messages),
        }),
        storage: createJSONStorage(() => localStorage),
      }
    ),
    { name: STORAGE_KEY }
  )
)
